import { USER_NAVIGATION_BUILT_IN_WORKSPACES } from "@repo/contracts";
import { create } from "zustand";
import { persist } from "zustand/middleware";

const BUILT_IN_WORKSPACES = new Set(USER_NAVIGATION_BUILT_IN_WORKSPACES);
const MAX_FAVORITES = 100;
const MAX_CUSTOM_WORKSPACES = 25;
const MAX_WORKSPACE_PAGES = 100;

function isLocalPageUrl(url) {
  if (
    typeof url !== "string"
    || !url.startsWith("/")
    || url.startsWith("//")
    || url.includes("\\")
    || url.length > 256
  ) {
    return false;
  }
  return ![...url].some((character) => {
    const code = character.charCodeAt(0);
    return /\s/.test(character) || code < 32 || code === 127;
  });
}

function normalizePageUrls(pageUrls, maximum = MAX_WORKSPACE_PAGES) {
  if (!Array.isArray(pageUrls)) return [];
  return [...new Set(pageUrls.filter(isLocalPageUrl))].slice(0, maximum);
}

function normalizeCustomWorkspaces(customWorkspaces) {
  if (!Array.isArray(customWorkspaces)) return [];

  const seenIds = new Set();
  const normalized = [];
  for (const workspace of customWorkspaces) {
    if (normalized.length >= MAX_CUSTOM_WORKSPACES) break;
    const id = typeof workspace?.id === "string" ? workspace.id.trim() : "";
    const name = typeof workspace?.name === "string"
      ? workspace.name.trim().slice(0, 48)
      : "";
    const pageUrls = normalizePageUrls(workspace?.pageUrls);
    if (
      id.length > 128
      || !/^custom:[A-Za-z0-9_-]+$/.test(id)
      || !name
      || !pageUrls.length
      || seenIds.has(id)
    ) {
      continue;
    }
    seenIds.add(id);
    normalized.push({ id, name, pageUrls });
  }
  return normalized;
}

export function resolveSidebarWorkspacePreferences(preferences) {
  const customWorkspaces = normalizeCustomWorkspaces(preferences?.customWorkspaces);
  const requestedWorkspace = typeof preferences?.activeWorkspace === "string"
    ? preferences.activeWorkspace
    : "all";
  const activeWorkspace = BUILT_IN_WORKSPACES.has(requestedWorkspace)
    || customWorkspaces.some(({ id }) => id === requestedWorkspace)
    ? requestedWorkspace
    : "all";

  return {
    activeWorkspace,
    favoritePageUrls: normalizePageUrls(preferences?.favoritePageUrls, MAX_FAVORITES),
    customWorkspaces,
  };
}

function updateWorkspacePreferenceState(state, scopeKey, preferences, syncPending = true) {
  return {
    workspacePreferencesByScope: {
      ...state.workspacePreferencesByScope,
      [scopeKey]: resolveSidebarWorkspacePreferences(preferences),
    },
    workspacePreferenceSyncPendingByScope: {
      ...state.workspacePreferenceSyncPendingByScope,
      [scopeKey]: syncPending,
    },
    workspacePreferenceRevisionsByScope: {
      ...state.workspacePreferenceRevisionsByScope,
      [scopeKey]: (state.workspacePreferenceRevisionsByScope[scopeKey] || 0) + 1,
    },
  };
}

export const useSidebarPreferencesStore = create(
  persist(
    (set) => ({
      showIndicators: true,
      accordionMode: true,
      openOnHover: false,
      density: "comfortable",
      workspacePreferencesByScope: {},
      workspacePreferenceSyncPendingByScope: {},
      workspacePreferenceRevisionsByScope: {},
      setShowIndicators: (val) => set({ showIndicators: val }),
      setAccordionMode: (val) => set({ accordionMode: val }),
      setOpenOnHover: (val) => set({ openOnHover: val }),
      setDensity: (val) => set({ density: val }),
      setActiveWorkspace: (scopeKey, activeWorkspace) => {
        if (!scopeKey || typeof activeWorkspace !== "string") return;
        set((state) => {
          const current = resolveSidebarWorkspacePreferences(
            state.workspacePreferencesByScope[scopeKey],
          );
          const next = resolveSidebarWorkspacePreferences({ ...current, activeWorkspace });
          return next.activeWorkspace === current.activeWorkspace
            ? state
            : updateWorkspacePreferenceState(state, scopeKey, next);
        });
      },
      toggleFavoritePage: (scopeKey, url) => {
        if (!scopeKey || !isLocalPageUrl(url)) return;
        set((state) => {
          const current = resolveSidebarWorkspacePreferences(
            state.workspacePreferencesByScope[scopeKey],
          );
          return updateWorkspacePreferenceState(state, scopeKey, {
            ...current,
            favoritePageUrls: current.favoritePageUrls.includes(url)
              ? current.favoritePageUrls.filter((item) => item !== url)
              : [...current.favoritePageUrls, url],
          });
        });
      },
      createCustomWorkspace: (scopeKey, name, pageUrls) => {
        const workspace = {
          id: `custom:${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`,
          name: String(name || "").trim().slice(0, 48),
          pageUrls: normalizePageUrls(pageUrls),
        };
        if (!scopeKey || !workspace.name || workspace.pageUrls.length === 0) return null;

        let created = false;
        set((state) => {
          const current = resolveSidebarWorkspacePreferences(
            state.workspacePreferencesByScope[scopeKey],
          );
          if (current.customWorkspaces.length >= MAX_CUSTOM_WORKSPACES) return state;
          created = true;
          return updateWorkspacePreferenceState(state, scopeKey, {
            ...current,
            customWorkspaces: [...current.customWorkspaces, workspace],
          });
        });
        return created ? workspace.id : null;
      },
      updateCustomWorkspace: (scopeKey, id, name, pageUrls) => {
        const nextName = String(name || "").trim().slice(0, 48);
        const nextPageUrls = normalizePageUrls(pageUrls);
        if (!scopeKey || !nextName || !nextPageUrls.length) return;

        set((state) => {
          const current = resolveSidebarWorkspacePreferences(
            state.workspacePreferencesByScope[scopeKey],
          );
          if (!current.customWorkspaces.some((workspace) => workspace.id === id)) return state;
          return updateWorkspacePreferenceState(state, scopeKey, {
            ...current,
            customWorkspaces: current.customWorkspaces.map((workspace) => (
              workspace.id === id
                ? { ...workspace, name: nextName, pageUrls: nextPageUrls }
                : workspace
            )),
          });
        });
      },
      deleteCustomWorkspace: (scopeKey, id) => {
        if (!scopeKey) return;
        set((state) => {
          const current = resolveSidebarWorkspacePreferences(
            state.workspacePreferencesByScope[scopeKey],
          );
          if (!current.customWorkspaces.some((workspace) => workspace.id === id)) return state;
          return updateWorkspacePreferenceState(state, scopeKey, {
            ...current,
            customWorkspaces: current.customWorkspaces.filter(
              (workspace) => workspace.id !== id,
            ),
            activeWorkspace: current.activeWorkspace === id ? "all" : current.activeWorkspace,
          });
        });
      },
      setWorkspacePreferencesFromServer: (scopeKey, preferences) => {
        if (!scopeKey) return;
        set((state) => updateWorkspacePreferenceState(
          state,
          scopeKey,
          preferences,
          false,
        ));
      },
      markWorkspacePreferenceSyncPending: (scopeKey) => {
        if (!scopeKey) return;
        set((state) => state.workspacePreferenceSyncPendingByScope[scopeKey]
          ? state
          : {
            workspacePreferenceSyncPendingByScope: {
              ...state.workspacePreferenceSyncPendingByScope,
              [scopeKey]: true,
            },
          });
      },
      markWorkspacePreferencesSynced: (scopeKey, revision) => {
        if (!scopeKey) return;
        set((state) => (
          (state.workspacePreferenceRevisionsByScope[scopeKey] || 0) !== revision
            ? state
            : {
              workspacePreferenceSyncPendingByScope: {
                ...state.workspacePreferenceSyncPendingByScope,
                [scopeKey]: false,
              },
            }
        ));
      },
    }),
    {
      name: "sidebar-preferences-storage",
      version: 7,
      migrate: (persistedState, version) => {
        const state = persistedState || {};
        if (version < 2) {
          try {
            localStorage.removeItem("sidebar-framework-filter");
          } catch {
            // ignore
          }
        }
        if (version < 3) {
          delete state.roadmapVisitedSteps;
        }
        if (version < 4) {
          delete state.dismissedFrameworkSuggestions;
        }
        if (version < 5) {
          delete state.viewMode;
        }
        if (version < 7) {
          delete state.selectedFramework;
        }
        state.workspacePreferencesByScope = state.workspacePreferencesByScope || {};
        state.workspacePreferenceSyncPendingByScope =
          state.workspacePreferenceSyncPendingByScope || {};
        return state;
      },
      partialize: (state) => ({
        showIndicators: state.showIndicators,
        accordionMode: state.accordionMode,
        openOnHover: state.openOnHover,
        density: state.density,
        workspacePreferencesByScope: state.workspacePreferencesByScope,
        workspacePreferenceSyncPendingByScope: state.workspacePreferenceSyncPendingByScope,
      }),
    },
  ),
);
